import { useEffect, useMemo, useRef, useState } from 'react';
import { FileUp, History, Info, Upload, X } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  LPO_LOI_STAGE, TYPE_OPTIONS, TYPE_STAGE, documentTypeLabel, findDocumentGroup, formatFileSize, guessFromFileName,
  nearTitle, reviewLabel, reviewStatusOf, titleSuggestions,
} from './documentsModel';
import { ProjectSelectField } from './ProjectPicker';
import { DOCUMENT_ACCEPT } from './ui';

// The api instance defaults to JSON; uploads must say multipart or axios turns FormData into JSON.
const MULTIPART = { 'Content-Type': 'multipart/form-data' };
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // backend upload_utils DEFAULT_MAX_UPLOAD_BYTES
const NO_STAGE = '__none__';
// "Status of this version" (16 idea 8): the type's starting point unless the person says it went out already.
const REVIEW_DEFAULT = '__default__';
const REVIEW_CHOICES = [
  { value: 'submitted:consultant', label: 'Submitted to Consultant' },
  { value: 'submitted:client', label: 'Submitted to Client' },
  { value: 'draft', label: 'Draft — not sent yet' },
  { value: 'for_record', label: 'For record — no approval needed' },
];
const ACCEPTED = new Set(DOCUMENT_ACCEPT.split(',').map((ext) => ext.trim().toLowerCase()));

/** Why a file can't be uploaded ('' when it can) — the same rules as the backend, said up front. */
export function fileProblem(file) {
  if (!file) return '';
  const name = String(file.name || '');
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')).toLowerCase() : '';
  if (!ACCEPTED.has(ext)) return `${ext ? ext.slice(1).toUpperCase() : 'This'} files can't be uploaded — use PDF, Word, Excel, CSV or an image.`;
  if (file.size > MAX_UPLOAD_BYTES) return `This file is ${formatFileSize(file.size)} — the limit is 10 MB.`;
  if (file.size === 0) return 'This file is empty.';
  return '';
}

const fieldClass = 'h-9 w-full rounded-control border border-line bg-surface px-3 text-[13.5px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)]';
const labelClass = 'mb-1.5 block text-[12.5px] font-semibold text-ink-2';

// The drawer's own drop zone: its drag events stop here, so the page behind never handles the same drop.
const stopDrag = (event) => { event.preventDefault(); event.stopPropagation(); };

function Dropzone({ file, onFile, error }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  return (
    <div
      onDragEnter={(event) => { stopDrag(event); setDragging(true); }}
      onDragOver={(event) => { stopDrag(event); setDragging(true); }}
      onDragLeave={(event) => { event.stopPropagation(); setDragging(false); }}
      onDrop={(event) => {
        stopDrag(event);
        setDragging(false);
        const dropped = event.dataTransfer?.files?.[0];
        if (dropped) onFile(dropped);
      }}
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-[12px] border-[1.5px] border-dashed px-4 py-5 text-center text-[12.5px] text-ink-3 transition-colors',
        dragging ? 'border-[color:var(--brand-ink)] bg-brand-soft' : error ? 'border-[color:var(--status-red)]' : 'border-line-2'
      )}
      data-testid="upload-document-dropzone"
    >
      {file ? (
        <div className="flex w-full items-center gap-2.5 text-left">
          <span className="ui-icon-tile tone-navy h-8 w-8 rounded-lg" aria-hidden="true"><FileUp className="h-4 w-4" /></span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold text-ink">{file.name}</span>
            <span className="block text-[11.5px] text-ink-3">{formatFileSize(file.size)}</span>
          </span>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="shrink-0 rounded px-1.5 text-[12px] font-semibold text-brand-ink hover:underline"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={() => onFile(null)}
            className="grid h-7 w-7 shrink-0 place-items-center rounded text-ink-3 hover:bg-surface-hover hover:text-ink"
            aria-label="Remove file"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <>
          <Upload className="h-5 w-5 text-ink-3" aria-hidden="true" />
          <span className="text-[13px] text-ink-2">
            Drop a file here or{' '}
            <button type="button" className="font-semibold text-brand-ink hover:underline" onClick={() => inputRef.current?.click()}>
              browse
            </button>
          </span>
          <span className="text-[11.5px]">PDF, Word, Excel, CSV or images · up to 10 MB</span>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={DOCUMENT_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        data-testid="upload-document-file"
        onChange={(event) => {
          const picked = event.target.files?.[0];
          if (picked) onFile(picked);
          event.target.value = '';
        }}
      />
    </div>
  );
}

/**
 * "Upload document" (16 idea 2): file, project, type, title (with the titles already used), stage
 * (filled from the type) and remarks. When the project + title + type already exist, it says that the
 * file becomes the next version ("This will be saved as v3 of HVAC Layout Drawing · v2 stays as an older
 * version") — the same rule POST /api/documents uses.
 *
 * initial: { file, projectId, type, title, stageId } (a dropped file pre-fills what its name suggests).
 * dropped: { file, seq } — a file dropped on the open drawer outside its drop zone (or beside it). It is
 * taken like a chosen file: it never resets the form or overwrites what the person typed.
 * onUploaded(saved, { replacedVersion }) runs after the server saved it.
 */
export default function UploadDocumentPanel({
  open, onOpenChange, initial, dropped, docs, projectList, canSeeLpoLoi, onUploaded,
}) {
  const [file, setFile] = useState(null);
  const [projectId, setProjectId] = useState('');
  const [type, setType] = useState('');
  const [title, setTitle] = useState('');
  const [stageId, setStageId] = useState(NO_STAGE);
  const [stageTouched, setStageTouched] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [reviewChoice, setReviewChoice] = useState(REVIEW_DEFAULT);
  const [titleFocused, setTitleFocused] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [showErrors, setShowErrors] = useState(false);
  const titleRef = useRef(null);
  // What the file name filled in (so a replaced file may update it, but nothing the person typed).
  const autoFilledRef = useRef({});
  const projects = projectList?.projects;

  // Fresh form each time the panel opens; a dropped file suggests title, type and project.
  useEffect(() => {
    if (!open) return;
    const guess = initial?.file ? guessFromFileName(initial.file.name, projects || []) : {};
    setFile(initial?.file || null);
    setProjectId(initial?.projectId || guess.projectId || '');
    setType(initial?.type || guess.type || '');
    setTitle(initial?.title || guess.title || '');
    autoFilledRef.current = {
      title: initial?.title ? undefined : guess.title,
      type: initial?.type ? undefined : guess.type,
      projectId: initial?.projectId ? undefined : guess.projectId,
    };
    setStageId(initial?.stageId || NO_STAGE);
    setStageTouched(Boolean(initial?.stageId));
    setRemarks('');
    setReviewChoice(REVIEW_DEFAULT);
    setShowErrors(false);
    setActiveSuggestion(-1);
    // projects arrive later than the file: only the first fill counts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  // The project list may load after the file was chosen: suggest the project then, unless one was picked.
  useEffect(() => {
    if (!open || projectId || !file || !projects?.length) return;
    const guess = guessFromFileName(file.name, projects);
    if (guess.projectId) {
      setProjectId(guess.projectId);
      autoFilledRef.current = { ...autoFilledRef.current, projectId: guess.projectId };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  const project = (projects || []).find((item) => item.id === projectId) || null;
  const stages = useMemo(
    () => (project?.stages || []).filter((stage) => canSeeLpoLoi || stage.name !== LPO_LOI_STAGE),
    [project, canSeeLpoLoi]
  );
  const group = findDocumentGroup(docs, { projectId, title, type });
  const near = group ? '' : nearTitle(docs, { projectId, title, type });
  const suggestions = useMemo(
    () => (projectId ? titleSuggestions(docs, { projectId, type, query: title }) : []),
    [docs, projectId, type, title]
  );
  const showSuggestions = titleFocused && suggestions.length > 0
    && !suggestions.some((doc) => String(doc.title).trim() === title.trim() && doc.type === type);

  // Stage follows the document: an existing document's stage, else the stage of its type — until the
  // person picks one themselves.
  useEffect(() => {
    if (stageTouched) return;
    const fromGroup = group?.latest?.stage_id && stages.some((stage) => stage.id === group.latest.stage_id) ? group.latest.stage_id : '';
    const wanted = TYPE_STAGE[type];
    const fromType = wanted ? stages.find((stage) => stage.name === wanted)?.id : '';
    setStageId(fromGroup || fromType || NO_STAGE);
  }, [stages, type, group?.latest?.stage_id, stageTouched]);

  const problem = fileProblem(file);
  const missing = {
    file: !file,
    project: !projectId,
    type: !type,
    title: !title.trim(),
  };
  const dirty = Boolean(file || title.trim() || remarks.trim());

  // A chosen file fills title, type and project from its name — only fields that are empty or were filled
  // from the previous file; what the person typed or picked is never overwritten.
  const takeFile = (next) => {
    setFile(next);
    if (!next) return;
    const guess = guessFromFileName(next.name, projects || []);
    const auto = autoFilledRef.current;
    const filled = {};
    if (guess.title && (!title.trim() || title === auto.title)) { setTitle(guess.title); filled.title = guess.title; }
    if (guess.type && (!type || type === auto.type)) { setType(guess.type); filled.type = guess.type; }
    if (guess.projectId && (!projectId || projectId === auto.projectId)) { setProjectId(guess.projectId); filled.projectId = guess.projectId; }
    autoFilledRef.current = filled;
  };

  useEffect(() => {
    if (open && dropped?.file) takeFile(dropped.file);
    // only a new drop counts (seq), not re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropped]);

  const pickSuggestion = (doc) => {
    setTitle(String(doc.title).trim());
    if (doc.type !== type) setType(doc.type);
    setActiveSuggestion(-1);
    setTitleFocused(false);
  };

  const save = async () => {
    setShowErrors(true);
    if (missing.file) throw new Error('Choose the file to upload');
    if (problem) throw new Error(problem);
    if (missing.project || missing.type || missing.title) throw new Error('Fill in the project, type and title');
    const form = new FormData();
    form.append('project_id', projectId);
    form.append('stage_id', stageId === NO_STAGE ? '' : stageId);
    form.append('title', title.trim());
    form.append('doc_type', type);
    form.append('remarks', remarks.trim());
    form.append('file', file);
    if (reviewChoice !== REVIEW_DEFAULT) {
      const [status, party] = reviewChoice.split(':');
      form.append('review_status', status);
      if (party) form.append('review_party', party);
    }
    const response = await api.post('/documents', form, { headers: MULTIPART });
    await onUploaded?.(response.data || {}, { replacedVersion: group?.latest?.version || null });
  };

  const saveLabel = group ? `Upload as v${group.nextVersion}` : 'Upload document';

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      icon={Upload}
      title="Upload document"
      subtitle="Files stay with their project and keep every version."
      dirty={dirty}
      onSave={save}
      saveLabel={saveLabel}
      saveDisabled={Boolean(problem)}
      confirmOptions={{ title: 'Discard this upload?', description: 'The file and the details you entered are not saved.', confirmLabel: 'Discard' }}
      testId="upload-document-panel"
    >
      <SidePanelSection title="File">
        <Dropzone file={file} onFile={takeFile} error={Boolean(problem) || (showErrors && missing.file)} />
        {problem ? <p role="alert" className="-mt-1 text-[12.5px] font-medium text-status-red-ink">{problem}</p> : null}
      </SidePanelSection>

      <SidePanelSection title="Details">
        <div>
          <label htmlFor="upload-project" className={labelClass}>Project</label>
          <ProjectSelectField
            id="upload-project"
            value={projectId}
            projects={projects}
            loading={projectList?.loading}
            error={projectList?.error}
            onRetry={projectList?.retry}
            onChange={(next) => { setProjectId(next.id); setStageTouched(false); }}
            invalid={showErrors && missing.project}
            testId="upload-document-project"
          />
        </div>

        <div>
          <label htmlFor="upload-type" className={labelClass}>Type</label>
          <Select value={type || ''} onValueChange={(next) => { setType(next); setStageTouched(false); }}>
            <SelectTrigger
              id="upload-type"
              className={cn('h-9 rounded-control text-[13.5px]', showErrors && missing.type && 'border-[color:var(--status-red)]')}
              data-testid="upload-document-type"
            >
              <SelectValue placeholder="Choose a type" />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative">
          <label htmlFor="upload-title" className={labelClass}>Title</label>
          <input
            ref={titleRef}
            id="upload-title"
            type="text"
            value={title}
            maxLength={200}
            autoComplete="off"
            role="combobox"
            aria-expanded={showSuggestions}
            aria-controls="upload-title-suggestions"
            aria-autocomplete="list"
            aria-activedescendant={showSuggestions && activeSuggestion >= 0 ? `upload-title-option-${activeSuggestion}` : undefined}
            aria-invalid={(showErrors && missing.title) || undefined}
            placeholder={projectId ? 'e.g. HVAC Layout Drawing — or pick an existing title' : 'e.g. HVAC Layout Drawing'}
            onChange={(event) => { setTitle(event.target.value); setActiveSuggestion(-1); }}
            onFocus={() => setTitleFocused(true)}
            onBlur={() => window.setTimeout(() => setTitleFocused(false), 120)}
            onKeyDown={(event) => {
              if (!showSuggestions) return;
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveSuggestion((index) => Math.min(index + 1, suggestions.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveSuggestion((index) => Math.max(index - 1, 0));
              } else if (event.key === 'Enter' && activeSuggestion >= 0) {
                event.preventDefault();
                pickSuggestion(suggestions[activeSuggestion]);
              } else if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setTitleFocused(false);
              }
            }}
            className={cn(fieldClass, showErrors && missing.title && 'border-[color:var(--status-red)]')}
            data-testid="upload-document-title"
          />
          {showSuggestions ? (
            <div
              id="upload-title-suggestions"
              role="listbox"
              aria-label="Titles already used in this project"
              className="absolute inset-x-0 top-full z-20 mt-1 rounded-xl border border-line bg-surface p-1.5 shadow-overlay"
            >
              <p className="px-2 pb-1 pt-0.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Already in this project</p>
              {suggestions.map((doc, index) => (
                <button
                  key={doc.id}
                  id={`upload-title-option-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === activeSuggestion}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => pickSuggestion(doc)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] hover:bg-surface-hover',
                    index === activeSuggestion && 'bg-surface-hover'
                  )}
                >
                  <span className="min-w-0 flex-1 truncate font-medium text-ink">{doc.title}</span>
                  <span className="shrink-0 text-[11.5px] text-ink-3">{documentTypeLabel(doc.type)}</span>
                  <span className="shrink-0 font-mono text-[11.5px] font-semibold text-ink-2">v{doc.version || 1}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {group ? (
          <div className="flex items-start gap-2.5 rounded-xl border border-[color:var(--brand-soft-border)] bg-brand-soft px-3 py-2.5 text-[12.5px] text-ink-2" data-testid="upload-document-version-note">
            <History className="mt-0.5 h-4 w-4 shrink-0 text-brand-ink" aria-hidden="true" />
            <p>
              This will be saved as <span className="font-mono font-bold text-ink">v{group.nextVersion}</span> of{' '}
              <span className="font-semibold text-ink">{group.latest.title}</span>
              {' · '}
              <span className="font-mono">v{group.latest.version || 1}</span> stays as an older version.
            </p>
          </div>
        ) : near ? (
          <div className="flex items-start gap-2.5 rounded-xl border border-line bg-surface-2 px-3 py-2.5 text-[12.5px] text-ink-2" data-testid="upload-document-near-title">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-status-amber" aria-hidden="true" />
            <p>
              “{near}” already exists. Use exactly that title to save this file as its next version.{' '}
              <button type="button" className="font-semibold text-brand-ink hover:underline" onClick={() => setTitle(near)}>
                Use “{near}”
              </button>
            </p>
          </div>
        ) : projectId && type && title.trim() ? (
          <p className="text-[12px] text-ink-3" data-testid="upload-document-new-note">New document · saved as <span className="font-mono font-semibold">v1</span></p>
        ) : null}

        <div>
          <label htmlFor="upload-stage" className={labelClass}>
            Stage <span className="font-normal text-ink-3">{TYPE_STAGE[type] && !stageTouched ? '(from the type)' : '(optional)'}</span>
          </label>
          <Select
            value={stageId}
            onValueChange={(next) => { setStageId(next); setStageTouched(true); }}
            disabled={!project}
          >
            <SelectTrigger id="upload-stage" className="h-9 rounded-control text-[13.5px]" data-testid="upload-document-stage">
              <SelectValue placeholder={project ? 'No stage' : 'Choose a project first'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_STAGE}>No stage</SelectItem>
              {stages.map((stage) => (
                <SelectItem key={stage.id} value={stage.id}>{stage.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label htmlFor="upload-review" className={labelClass}>
            Status of this version <span className="font-normal text-ink-3">(optional)</span>
          </label>
          <Select value={reviewChoice} onValueChange={setReviewChoice}>
            <SelectTrigger id="upload-review" className="h-9 rounded-control text-[13.5px]" data-testid="upload-document-review">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={REVIEW_DEFAULT}>{type ? `${reviewLabel(reviewStatusOf({ type }))} (usual for ${documentTypeLabel(type)})` : 'Usual for the type'}</SelectItem>
              {REVIEW_CHOICES.map((choice) => <SelectItem key={choice.value} value={choice.value}>{choice.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label htmlFor="upload-remarks" className={labelClass}>
            {group ? 'What changed?' : 'Remarks'} <span className="font-normal text-ink-3">(optional)</span>
          </label>
          <textarea
            id="upload-remarks"
            value={remarks}
            maxLength={2000}
            rows={3}
            onChange={(event) => setRemarks(event.target.value)}
            placeholder={group ? 'e.g. Updated per consultant comments' : 'e.g. Issued for approval'}
            className={cn(fieldClass, 'h-auto min-h-[72px] resize-y py-2')}
            data-testid="upload-document-remarks"
          />
        </div>
      </SidePanelSection>
    </SidePanel>
  );
}
